import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { MatStep, MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { CountingMethod } from '@backend/features/counting-method/counting-methods';
import {
  exportLocationModes,
  geoAreas as geoAreasMap,
  IExportFilter,
  TExportLocationMode,
  TGeoAreaCode,
} from '@backend/features/export/export.cross-platform';
import {
  TPlussedValuesExportMode,
  TValueMode,
  defaultPlussedValuesExportMode,
  plussedValuesExportModeLabelList,
  valueModeLabelList,
} from '@backend/features/export/export.interface';
import { GeneralMessageService } from 'apps/mba-cpr-survey-portal/src/app/shared/services/general-message.service';
import { latLng, polygon, tileLayer } from 'leaflet';
import { pick } from 'lodash';
import { PermissionsService } from '../../../permissions/permissions.service';
import {
  ExportService,
  IExportMeasurementSettings,
  IExportSampleSettings,
} from '../../export.service';

type ExportType = 'sample' | 'measurement' | 'monthly-average';
type ExportSampleFormat = 'csv' | 'json';
type TaxaSelectionMode = 'countingMethods' | 'taxonIds';

const EUROPEAN_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: ['d/M/yyyy', 'dd/M/yyyy', 'd/MM/yyyy', 'dd/MM/yyyy'],
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

@Component({
  selector: 'app-export-create-screen',
  templateUrl: './export-create-screen.component.html',
  styleUrls: ['./export-create-screen.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: EUROPEAN_DATE_FORMATS }],
})
export class ExportCreateScreenComponent {
  private static readonly MAX_COORDINATE_DECIMALS = 8;
  private static readonly MIN_LATITUDE = -90;
  private static readonly MAX_LATITUDE = 90;
  private static readonly MIN_LONGITUDE = -180;
  private static readonly MAX_LONGITUDE = 180;

  readonly customAreaLocationMode = exportLocationModes[1];
  readonly predefinedAreaLocationMode = exportLocationModes[0];
  readonly towsLocationMode = exportLocationModes[2];
  map: L.Map;
  customPolygon: L.Polygon;
  geoAreas = Object.entries(geoAreasMap);
  isGeoStepActive = false;
  selectedStepIndex = 0;
  hasAttemptedStartExport = false;
  isProcessing = false;
  mapVisible = false;
  valueModeLables = valueModeLabelList;
  plussedValuesExportModeLabels = [...plussedValuesExportModeLabelList].reverse();

  @ViewChild('stepper') stepper: MatStepper;
  @ViewChild('geoStep') geoStep: MatStep;

  readonly typeForm = new FormGroup({
    type: new FormControl<ExportType>('sample', Validators.required),
  });

  readonly filterForm = new FormGroup({
    valueMode: new FormControl<TValueMode>('meanPerSample'),
    taxaSelectionMode: new FormControl<TaxaSelectionMode>('taxonIds', Validators.required),
    cprTaxonIds: new FormControl<string>(''),
    locationMode: new FormControl<TExportLocationMode>('predefined-area', Validators.required),
    geoCodes: new FormControl<TGeoAreaCode[]>([]),
    fromDate: new FormControl<Date>(null),
    toDate: new FormControl<Date>(null),
    eyeCountCountingMethod: new FormControl<Boolean>(false),
    traverseCountingMethod: new FormControl<Boolean>(false),
    phytoCountingMethod: new FormControl<Boolean>(false),
    includeUnfinalised: new FormControl<boolean>(false),
    spreadDate: new FormControl<boolean>(false),
    includeSunParameters: new FormControl<boolean>(false),
    includePCIValues: new FormControl<boolean>(false),
    plussedValuesExportMode: new FormControl<TPlussedValuesExportMode>(
      defaultPlussedValuesExportMode,
      Validators.required,
    ),
    polygonStr: new FormControl(''), //'80|-80,80|26,30|26,30|-80,80|-80'
    towSearchTermsText: new FormControl(''),
  });

  hasExportPermission = false;

  options: any = {
    layers: [tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png')],
    zoom: 10,
    center: latLng(46.879966, -121.726909),
  };

  polygon;

  private readonly polygonPairRegex = /(-?\d+(?:\.\d+)?)\s*(?:\||,)\s*(-?\d+(?:\.\d+)?)/g;

  private getDecimalPlaces(value: string): number {
    const decimalPart = value.split('.')[1];
    return decimalPart?.length ?? 0;
  }

  constructor(
    private exportService: ExportService,
    private generalMessageService: GeneralMessageService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private permissionsService: PermissionsService,
    @Inject(MAT_DATE_FORMATS) public dateFormats: MatDateFormats,
  ) {}

  async ngOnInit() {
    this.hasExportPermission = await this.permissionsService.hasPermission('export', 'export');
  }

  get isCustomAreaMode() {
    return this.filterForm.controls['locationMode'].value === this.customAreaLocationMode;
  }

  get isPredefinedAreaMode() {
    return this.filterForm.controls['locationMode'].value === this.predefinedAreaLocationMode;
  }

  get isTowsMode() {
    return this.filterForm.controls['locationMode'].value === this.towsLocationMode;
  }

  get isCountingMethodsMode() {
    return this.filterForm.controls['taxaSelectionMode'].value === 'countingMethods';
  }

  get isTaxonIdsMode() {
    return this.filterForm.controls['taxaSelectionMode'].value === 'taxonIds';
  }

  drawPolygon() {
    this.renderCustomPolygonOnMap();
  }

  onMapReady(map: L.Map) {
    this.map = map;
    this.renderCustomPolygonOnMap();
  }

  getTowSearchTerms() {
    return this.filterForm.controls['towSearchTermsText'].value
      ?.split(/[\n,]+/)
      .map((value) => value.trim())
      .filter((value) => !!value);
  }

  private clearPolygonError() {
    const polygonControl = this.filterForm.controls['polygonStr'];
    if (!polygonControl.errors) {
      return;
    }

    const { invalidPolygon, server, ...rest } = polygonControl.errors;
    const nextErrors = Object.keys(rest).length > 0 ? rest : null;
    polygonControl.setErrors(nextErrors);
  }

  private setPolygonError(message: string) {
    const polygonControl = this.filterForm.controls['polygonStr'];
    polygonControl.setErrors({ ...(polygonControl.errors ?? {}), invalidPolygon: message });
    polygonControl.markAsTouched();
  }

  private closePolygon<T extends [number, number]>(points: T[]): T[] {
    if (points.length === 0) {
      return points;
    }

    const [firstLat, firstLng] = points[0];
    const [lastLat, lastLng] = points[points.length - 1];
    if (firstLat === lastLat && firstLng === lastLng) {
      return points;
    }

    return [...points, points[0]];
  }

  private parsePolygonInput():
    | {
        mapPolygon: [number, number][];
        apiPolygon: [number, number][];
      }
    | null {
    const rawValue = this.filterForm.controls['polygonStr'].value?.trim();
    this.clearPolygonError();

    if (!rawValue) {
      return null;
    }

    const matches = [...rawValue.matchAll(this.polygonPairRegex)];
    const unmatchedCharacters = rawValue.replace(this.polygonPairRegex, '').replace(/[\s;,]+/g, '');
    if (matches.length === 0 || unmatchedCharacters.length > 0) {
      this.setPolygonError(
        'Enter coordinates as lat,lng pairs separated by semicolons or new lines.',
      );
      return null;
    }

    const mapPolygon = matches.map((match) => [Number(match[1]), Number(match[2])] as [number, number]);
    if (mapPolygon.some(([lat, lng]) => Number.isNaN(lat) || Number.isNaN(lng))) {
      this.setPolygonError('Each coordinate pair must contain valid numbers.');
      return null;
    }

    const hasOverPrecisionCoordinate = matches.some(
      (match) =>
        this.getDecimalPlaces(match[1]) > ExportCreateScreenComponent.MAX_COORDINATE_DECIMALS ||
        this.getDecimalPlaces(match[2]) > ExportCreateScreenComponent.MAX_COORDINATE_DECIMALS,
    );
    if (hasOverPrecisionCoordinate) {
      this.setPolygonError(
        `Use up to ${ExportCreateScreenComponent.MAX_COORDINATE_DECIMALS} decimal places for latitude and longitude.`,
      );
      return null;
    }

    const hasOutOfRangeCoordinates = mapPolygon.some(
      ([lat, lng]) =>
        lat < ExportCreateScreenComponent.MIN_LATITUDE ||
        lat > ExportCreateScreenComponent.MAX_LATITUDE ||
        lng < ExportCreateScreenComponent.MIN_LONGITUDE ||
        lng > ExportCreateScreenComponent.MAX_LONGITUDE,
    );
    if (hasOutOfRangeCoordinates) {
      this.setPolygonError('Latitude must be between -90 and 90, and longitude between -180 and 180.');
      return null;
    }

    if (mapPolygon.length < 3) {
      this.setPolygonError('Enter at least three coordinate pairs to define an area.');
      return null;
    }

    const closedMapPolygon = this.closePolygon(mapPolygon);
    return {
      mapPolygon: closedMapPolygon,
      apiPolygon: closedMapPolygon.map(([lat, lng]) => [lng, lat] as [number, number]),
    };
  }

  get polygonErrorMessage() {
    const errors = this.filterForm.controls['polygonStr'].errors;
    return errors?.invalidPolygon ?? errors?.server ?? null;
  }

  get taxaSelectionErrorMessage() {
    return this.hasSelectedTaxa
      ? null
      : 'Select at least one counting category, or enter one or more Taxon IDs.';
  }

  get locationSelectionErrorMessage() {
    return this.hasSelectedLocation ? null : 'Select at least one location.';
  }

  get fromDateErrorMessage() {
    return this.filterForm.controls['fromDate'].value ? null : 'Select a start date.';
  }

  get showTaxaStepError() {
    return !!this.taxaSelectionErrorMessage && (this.hasAttemptedStartExport || this.selectedStepIndex === 1);
  }

  get showLocationStepError() {
    return (
      !!this.locationSelectionErrorMessage &&
      (this.hasAttemptedStartExport || this.selectedStepIndex === 2)
    );
  }

  get showDateStepError() {
    return !!this.fromDateErrorMessage && (this.hasAttemptedStartExport || this.selectedStepIndex === 3);
  }

  private get hasSelectedTaxa() {
    if (this.isTaxonIdsMode) {
      return !!this.filterForm.controls['cprTaxonIds'].value?.trim();
    }

    return (
      !!this.filterForm.controls['eyeCountCountingMethod'].value ||
      !!this.filterForm.controls['traverseCountingMethod'].value ||
      !!this.filterForm.controls['phytoCountingMethod'].value
    );
  }

  private get hasSelectedLocation() {
    if (this.isPredefinedAreaMode) {
      return !!this.filterForm.controls['geoCodes'].value?.length;
    }

    if (this.isCustomAreaMode) {
      return !!this.filterForm.controls['polygonStr'].value?.trim();
    }

    return !!this.getTowSearchTerms()?.length;
  }

  get confirmStepErrors() {
    return [
      this.taxaSelectionErrorMessage,
      this.locationSelectionErrorMessage,
      this.fromDateErrorMessage,
      this.polygonErrorMessage,
    ].filter((message): message is string => !!message);
  }

  get hasKnownBlockingValidationErrors() {
    return !!(
      this.taxaSelectionErrorMessage ||
      this.locationSelectionErrorMessage ||
      this.fromDateErrorMessage ||
      this.polygonErrorMessage
    );
  }

  private getFirstInvalidStepIndex() {
    if (this.taxaSelectionErrorMessage) {
      return 1;
    }

    if (this.locationSelectionErrorMessage || this.polygonErrorMessage) {
      return 2;
    }

    if (this.fromDateErrorMessage) {
      return 3;
    }

    return -1;
  }

  validatePolygonInput() {
    this.parsePolygonInput();
    if (!this.mapVisible || !this.map?.getContainer?.() || !this.map.getContainer().isConnected) {
      return;
    }

    this.renderCustomPolygonOnMap();
  }

  private renderCustomPolygonOnMap() {
    if (!this.map || !this.isCustomAreaMode || !this.mapVisible) {
      return;
    }

    const mapContainer = this.map.getContainer?.();
    if (!mapContainer || !mapContainer.isConnected) {
      return;
    }

    if (this.customPolygon) {
      this.map.removeLayer(this.customPolygon);
      this.customPolygon = null;
    }

    const parsedPolygon = this.parsePolygonInput();
    if (!parsedPolygon) {
      return;
    }

    this.customPolygon = polygon(parsedPolygon.mapPolygon).addTo(this.map);
    this.map.fitBounds(this.customPolygon.getBounds());
  }

  getFilterValues() {
    const filterFormValues = this.filterForm.value as any;
    let cprTaxonIds: number[];
    if (filterFormValues.cprTaxonIds && filterFormValues.cprTaxonIds != '') {
      cprTaxonIds = filterFormValues.cprTaxonIds.split(',').map((s) => +s.trim());
    }
    const parsedPolygon = this.parsePolygonInput();

    let filters = {
      ...pick(filterFormValues, [
        'valueMode',
        'fromDate',
        'toDate',
        'includeUnfinalised',
        'spreadDate',
        'includeSunParameters',
        'includePCIValues',
        'plussedValuesExportMode',
        'locationMode',
        'geoCodes',
      ]),
      cprTaxonIds,
    } as IExportFilter;

    filters.countingMethods = (
      [
        ['EYECOUNT', filterFormValues.eyeCountCountingMethod],
        ['TRAVERSE', filterFormValues.traverseCountingMethod],
        ['PHYTO', filterFormValues.phytoCountingMethod],
      ] as [CountingMethod, Boolean][]
    )
      .filter(([, value]) => !!value)
      .map(([code]) => code);

    if (parsedPolygon?.apiPolygon?.length) {
      filters.polygon = parsedPolygon.apiPolygon;
    }

    const towSearchTerms = this.getTowSearchTerms();
    if (towSearchTerms?.length) {
      filters.towSearchTerms = towSearchTerms;
    }

    return filters;
  }

  async startExport() {
    this.hasAttemptedStartExport = true;
    this.validatePolygonInput();

    if (this.confirmStepErrors.length) {
      const firstInvalidStepIndex = this.getFirstInvalidStepIndex();
      if (firstInvalidStepIndex >= 0 && this.stepper) {
        this.stepper.selectedIndex = firstInvalidStepIndex;
      }
      return;
    }

    this.isProcessing = true;
    try {
      const filter = this.getFilterValues();
      if (this.isCustomAreaMode && this.filterForm.controls['polygonStr'].value && !filter.polygon) {
        return;
      }

      const type = this.typeForm.controls['type'].value;
      if (type == 'sample') {
        const settings: IExportSampleSettings = {
          filter,
        };
        await this.exportService.startSampleExport(settings);
      } else if (type == 'measurement') {
        const settings: IExportMeasurementSettings = {
          filter,
        };
        await this.exportService.startMeasurementExport(settings);
      } else if (type == 'monthly-average') {
        const settings: IExportMeasurementSettings = {
          filter,
        };
        await this.exportService.startMonthlyAverageExport(settings);
      } else {
        throw new Error('Invalid export type');
      }
      this.generalMessageService.success('The export has been started.');
      await this.router.navigate(['export'], { queryParams: { poll: '1' } });
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        const messages = Array.isArray(error.error?.message)
          ? error.error.message
          : [error.error?.message].filter((message): message is string => !!message);
        const polygonMessage = messages.find((message) => message.includes('polygon'));
        if (polygonMessage) {
          const polygonControl = this.filterForm.controls['polygonStr'];
          polygonControl.setErrors({ ...(polygonControl.errors ?? {}), server: polygonMessage });
          polygonControl.markAsTouched();
        }
      }
      throw error;
    } finally {
      this.isProcessing = false;
    }
  }

  predefinedGeographicRegionChange() {
    this.filterForm.controls['polygonStr'].setValue(null);
    this.filterForm.controls['towSearchTermsText'].setValue(null);
  }

  onTaxaSelectionModeChange() {
    if (this.isCountingMethodsMode) {
      this.filterForm.controls['cprTaxonIds'].setValue('');
      return;
    }

    this.filterForm.patchValue({
      eyeCountCountingMethod: false,
      traverseCountingMethod: false,
      phytoCountingMethod: false,
    });
  }

  onTaxonIdsChange() {
    const taxonIds = this.filterForm.controls['cprTaxonIds'].value?.trim();
    if (!taxonIds) {
      return;
    }

    if (!this.isTaxonIdsMode) {
      this.filterForm.controls['taxaSelectionMode'].setValue('taxonIds');
    }

    this.filterForm.patchValue({
      eyeCountCountingMethod: false,
      traverseCountingMethod: false,
      phytoCountingMethod: false,
    });
  }

  onCountingMethodChange() {
    if (!this.isCountingMethodsMode) {
      this.filterForm.controls['taxaSelectionMode'].setValue('countingMethods');
    }

    this.filterForm.controls['cprTaxonIds'].setValue('');
  }

  customGeographicRegionChange() {
    this.clearPolygonError();
    this.filterForm.controls['geoCodes'].setValue(null);
    this.filterForm.controls['towSearchTermsText'].setValue(null);
  }

  towSearchTermsChange() {
    this.filterForm.controls['geoCodes'].setValue([]);
    this.filterForm.controls['polygonStr'].setValue(null);
  }

  onLocationModeChange() {
    const locationMode = this.filterForm.controls['locationMode'].value;

    if (locationMode !== this.predefinedAreaLocationMode) {
      this.filterForm.controls['geoCodes'].setValue([]);
    }

    if (locationMode !== this.customAreaLocationMode) {
      this.filterForm.controls['polygonStr'].setValue(null);
      if (this.customPolygon && this.map) {
        this.map.removeLayer(this.customPolygon);
        this.customPolygon = null;
      }
    }

    if (locationMode !== this.towsLocationMode) {
      this.filterForm.controls['towSearchTermsText'].setValue(null);
    }

    this.mapVisible = this.isGeoStepActive && this.isCustomAreaMode;
  }

  onTypeChange() {
    return;
  }

  onStepChange(ev) {
    this.selectedStepIndex = ev.selectedIndex;
    this.isGeoStepActive = ev.selectedStep == this.geoStep;

    if (this.isGeoStepActive && this.isCustomAreaMode) {
      this.validatePolygonInput();
    }

    this.mapVisible = false;

    setTimeout(() => {
      this.mapVisible = this.isGeoStepActive && this.isCustomAreaMode;
    });
  }
}
